import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setHeader } from "../../store/settings/settingsSlice";
import style from "./wallet.module.css";
import walletArrowBg from '../../assets/images/walletArrow.svg'
import bitcoinIcon from '../../assets/images/bitcoin.svg'
import ethIcon from "../../assets/images/ethereum-eth-logo.png";
import ltcIcon from "../../assets/images/litecoin-ltc-logo.svg";
import usdtIcon from "../../assets/images/tether-usdt-logo.svg";
import xrpIcon from "../../assets/images/xrp-xrp-logo.svg";
import dogeIcon from "../../assets/images/dogecoin-doge-logo.svg";
import trxIcon from "../../assets/images/tron-trx-logo.svg";
import bnbIcon from "../../assets/images/bnb-bnb-logo.svg";
import usdcIcon from "../../assets/images/usd-coin-usdc-logo.svg";
import solIcon from "../../assets/images/solana-sol-logo.svg";
import xlmIcon from "../../assets/images/stellar-xlm-logo.svg";
import ng_icon from "../../assets/images/ng.svg";
import ghana_icon from "../../assets/images/gh.svg";
import usd_icon from "../../assets/images/us.svg";
import { Eye, EyeSlash } from "iconsax-react";
import { useNavigate } from "react-router-dom";
import { getCryptoCoins } from "../../store/settings/settingsActions";
import { fetchExchangeConfig, getWalletBalance } from "../../store/wallet/walletActions";


const walletTabs = [
  { code: "FIAT", label: "Fiat" },
  { code: "CRYPTO", label: "Crypto" },
];


const coinIcons = {
  BTC: bitcoinIcon,
  ETH: ethIcon,
  LTC: ltcIcon,
  USDT: usdtIcon,
  XRP: xrpIcon,
  DOGE: dogeIcon,
  TRX: trxIcon,
  BNB: bnbIcon,
  USDC: usdcIcon,
  SOL: solIcon,
  XLM: xlmIcon,
};

const fiatAssetMeta = {
  NGN: {
    name: "Naira",
    symbol: "NGN",
    icon: ng_icon,
    pricePrefix: "₦",
  },
  GHS: {
    name: "Ghana Cedis",
    symbol: "GHS",
    icon: ghana_icon,
    pricePrefix: "GH₵",
  },
  USD: {
    name: "US Dollar",
    symbol: "USD",
    icon: usd_icon,
    pricePrefix: "$",
  },
};


const Wallets = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("FIAT");
  const { cryptocoins, loading } = useSelector((state) => state.settings);
  const walletData = useSelector((state) => state.wallet.data);
  const loadingWallet = useSelector((state) => state.wallet.loadingWallet);
  const exchangeConfig = useSelector((state) => state.wallet.exchangeConfig);
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();

   // Memoize fiatWallets to prevent unnecessary re-renders
  const fiatWallets = useMemo(() => {
    return walletData?.fiatWallets || [];
  }, [walletData?.fiatWallets]);

 // Helper function to get exchange rate from a currency to USD
  const getExchangeRateToUSD = useCallback((currencyCode) => {
    if (currencyCode === "USD") return 1;
    
    if (!exchangeConfig || !Array.isArray(exchangeConfig)) return 0;
    
    const config = exchangeConfig.find(
      (cfg) => 
        cfg.BaseCurrency?.code === currencyCode && 
        cfg.TargetCurrency?.code === "USD"
    );
    
    return config ? parseFloat(config.exchange_rate) : 0;
  }, [exchangeConfig]);

  // Calculate total fiat balance in USD
  const totalFiatInUSD = useMemo(() => {
    if (!fiatWallets || !Array.isArray(fiatWallets)) return 0;
    
    return fiatWallets.reduce((sum, wallet) => {
      const balance = parseFloat(wallet.balance) || 0;
      const currencyCode = wallet.Currency?.code;
      const exchangeRate = getExchangeRateToUSD(currencyCode);
      return sum + (balance * exchangeRate);
    }, 0);
  }, [fiatWallets, getExchangeRateToUSD]);

  // Calculate total crypto balance in USD
  const totalCryptoInUSD = useMemo(() => {
    if (!cryptocoins || !Array.isArray(cryptocoins)) return 0;
    
    return cryptocoins.reduce(
      (sum, coin) => sum + (parseFloat(coin.convertedBalanceUsd) || 0),
      0
    );
  }, [cryptocoins]);

  // Total portfolio balance in USD (fiat + crypto)
  const totalPortfolioBalanceUSD = useMemo(() => {
    return totalFiatInUSD + totalCryptoInUSD;
  }, [totalFiatInUSD, totalCryptoInUSD]);

  // Format USD balance
  const formattedPortfolioBalance = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totalPortfolioBalanceUSD);
  }, [totalPortfolioBalanceUSD]);

  // Map fiat wallets
  const mappedFiatWallets = fiatWallets
    .filter(wallet => wallet.is_active)
    .map(wallet => {
      const meta = fiatAssetMeta[wallet.Currency?.code] || {};
      return {
        code: wallet.Currency?.code,
        name: meta.name || wallet.Currency?.name || wallet.Currency?.code,
        symbol: meta.symbol || wallet.Currency?.code,
        icon: meta.icon,
        price: `${meta.pricePrefix || ""}${parseFloat(wallet.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        balance: wallet.balance,
        type: "FIAT",
      };
    });

  // Map crypto wallets
  const mappedCryptoWallets = (cryptocoins || []).map(coin => ({
    code: coin.currency?.toUpperCase(),
    name: coin.name,
    symbol: coin.currency?.toUpperCase(),
    icon: coinIcons[coin.currency?.toUpperCase()] || bitcoinIcon,
    price: `${coin.currency?.toUpperCase()} ${coin.balance || "0.00"}`,
    change: coin.price_change_percentage
      ? `${coin.price_change_percentage > 0 ? "+" : ""}${coin.price_change_percentage.toFixed(2)}%`
      : "0.00%",
    changeUp: coin.price_change_percentage > 0,
    type: "CRYPTO"
  }));

  // Filter wallets based on tab
  const filteredWallets = activeTab === "FIAT" ? mappedFiatWallets : mappedCryptoWallets;

  useEffect(() => {
    dispatch(setHeader("Wallets"));
    dispatch(getCryptoCoins());
    dispatch(getWalletBalance());
    dispatch(fetchExchangeConfig());
  }, [dispatch]);

  return (
    <>
    <div className={style.walletBalanceCard}>
        <div>
          <div className={style.walletBalanceLabel}>PORTFOLIO BALANCE</div>
          <div className={style.walletBalanceAmount}>
             {visible ? formattedPortfolioBalance : "******"}
            {visible === true ? (
                <EyeSlash
                    onClick={() => {
                    setVisible(!visible);
                    }}
                    className={style.walEye}
                    color="#fff"
                    variant="Bulk"
                    style={{cursor: 'pointer'}}
                />
                ) : (
                <Eye
                    onClick={() => {
                    setVisible(!visible);
                    }}
                    className={style.walEye}
                    color="#fff"
                    variant="Bulk"
                    style={{cursor: 'pointer'}}

                />
              )}
          </div>
        </div>
        <div className={style.walletBalanceCurrency}>
          <img src={usd_icon} alt="usd" className={style.walletFlag} />
          <span>USD</span>
        </div>

          <div className={style.walletArrowBg}>
              <img src={walletArrowBg} alt="wallet arrow" />
          </div>  
      </div>

      <div className={style.walletTabs}>
        {walletTabs.map(tab => (
          <button
            key={tab.code}
            className={`${style.walletTab} ${activeTab === tab.code ? style.activeTab : ""}`}
            onClick={() => setActiveTab(tab.code)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <hr className={style.walletDivider} />

    <div className={style.cryptoAssetsList} style={{paddingBottom: '40px'}}>
        {(activeTab === "FIAT" ? loadingWallet : loading) ? (
          <div style={{ width: "100%", textAlign: "center", padding: "40px 0" }}>
            Loading...
          </div>
        ) : filteredWallets.length === 0 ? (
          <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#909090" }}>
            No {activeTab === "FIAT" ? "fiat" : "crypto"} wallets found.
          </div>
        ) : (
          filteredWallets.map((wallet, idx) => (
            <div
              className={style.fiatAssetCard}
              key={wallet.code + idx}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (wallet.type === "CRYPTO") {
                  navigate(`/crypto-wallet?currency=${wallet.code}`);
                } else if (wallet.type === "FIAT") {
                  navigate(`/fiat-wallet?currency=${wallet.code}`);
                }
              }}
            >
              <div className={style.fiatAssetInfo}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  justifyContent: 'center',
                }}>
                  <img src={wallet.icon} alt={wallet.name} className={style.fiatAssetIcon} />
                </div>
                <div>
                  <div className={style.fiatAssetName}>{wallet.name}</div>
                  <div className={style.fiatAssetSymbol}>{wallet.symbol}</div>
                </div>
              </div>
              <div className={style.fiatAssetPriceBlock}>
                <div className={style.fiatAssetPrice}>{wallet.price}</div>
                {wallet.type === "CRYPTO" && (
                  <div
                    className={wallet.changeUp ? style.fiatAssetChangeUp : style.fiatAssetChangeDown}
                  >
                    {wallet.changeUp ? "↑" : "↓"} {wallet.change}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default Wallets;